import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useViewAs, ROLES_ALLOWED_TO_SWITCH } from '../../contexts/ViewAsContext';

/**
 * RoleRoute — client-side route guard.
 *
 * Renders children only when the authenticated user's role is included in
 * the `roles` array. Any other role is redirected to /dashboard.
 *
 * NOTE: this is a UX guard only. Real authorization is enforced server-side
 * by requireRole(['hod']) + scopeToDepartment middleware on every /api/hod/* route.
 *
 * Usage:
 *   <RoleRoute roles={['hod']}>
 *     <LeaveApprovals />
 *   </RoleRoute>
 */
const RoleRoute = ({ roles, children }) => {
  const { user, loading } = useAuth();
  const { viewAsRole } = useViewAs();

  // Honor "view as" preview: an admin/HOD previewing another role is allowed
  // into that role's routes (server-side requireRole is the real guard).
  const effectiveRole =
    user && ROLES_ALLOWED_TO_SWITCH.has(user.role) && viewAsRole ? viewAsRole : user?.role;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress sx={{ color: '#1e3a8a' }} />
      </Box>
    );
  }

  if (!user || !roles.includes(effectiveRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default RoleRoute;
